import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { FileDownloaded, GlobalActions } from '../../../model';
import walletRepository from '../../../repositories/wallet/wallet-repository';
import { messagesForWalletError, WalletError } from '../../../store';
import { getCurrentState } from '../../../store/features/wallet/my-wallet/my-wallet-selectors';
import { getKidUrl } from '../../../utils/api-utils';
import { useAppSelector } from '../../redux';

export interface MyWalletKidLogic {
  pdf?: FileDownloaded;
  url?: string;
  pdfLoading: boolean;
}

export const useMyWalletKidLogic = (
  globalActions: GlobalActions
): MyWalletKidLogic => {
  const { selectedProduct, productDetailsSelectedSection }:any =
    useSelector(getCurrentState);
  const token: string = useAppSelector((state) => state.data?.user?.token);
  const [content, setContent] = useState<FileDownloaded>();
  const [urlKid, setUrlKid] = useState<string>();
  const [pdfLoading, setPdfLoading] = useState(false);
  const [KidError, setKidError] = useState<WalletError>();
  const { formatMessage } = useIntl();
  const { showToast, closeToast } = globalActions;
  const kidCode = useMemo(()=>selectedProduct?.isin ?? selectedProduct?.rp ?? '',[selectedProduct]);
  const retrieveData = useCallback(
    async (documentIsin: string) => {
      try {
        return await walletRepository.downloadKidPdf(
          documentIsin
        );
      } catch (error) {
        setKidError('GENERIC_ERROR');
        return null;
      }
    },
    [token]
  );

  const downloadContent = useCallback(
    async (documentIsin: string) => {
      setPdfLoading(true);
      const blob = (await retrieveData(documentIsin)) as Blob;
      if (blob) {
        setContent({
          name: `${documentIsin}.pdf`,
          blob,
          mediaType: 'pdf',
          type: 'productDetails',
        });
        setUrlKid(undefined);
      }else {
        setContent(undefined);
        const url = getKidUrl(documentIsin);
        setUrlKid(url);
      }

      setPdfLoading(false);
    },
    [retrieveData]
  );

  useEffect(() => {
    if (kidCode !== '' && productDetailsSelectedSection === 'KID') {
      downloadContent(kidCode);
    }
  }, [downloadContent, kidCode, productDetailsSelectedSection]);

  useEffect(() => {
    if (KidError) {
      const errorMessages = messagesForWalletError(KidError);
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      setKidError(undefined);
    }
  }, [closeToast, formatMessage, KidError, showToast]);

  return {
    url: urlKid,
    pdf: content,
    pdfLoading,
  };
};
